'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Button } from '@benzinga/core-ui';
import { InputContainer, InputWrapper } from '../InputContainer';
import { SelectContainer, Option } from '../SelectContainer';
import { SessionContext } from '@benzinga/session-context';
import { ForexManager } from '@benzinga/forex-manager';
import { currencyPairs, majorCurrencies } from './utils';
import { SingleValue } from 'react-select';

const lotSizeInUnits = 100000;

enum TradeSizeMeasurement {
  Lots = 'Lots',
  Units = 'Units',
}

enum Direction {
  Buy = 'Buy',
  Sell = 'Sell',
}

interface CalculateResultParams {
  openPrice?: number;
  closePrice?: number;
  tradeSize?: number;
  direction?: Direction;
  currencyPair?: string;
  tradeSizeMeasurement?: TradeSizeMeasurement;
  accountCurrency?: string;
}

export const ForexProfitCalculator: React.FC = () => {
  const [exchangeRates, setExchangeRates] = React.useState<{ [key: string]: number }>({});
  const [currencyPair, setCurrencyPair] = React.useState<SingleValue<Option>>(currencyPairs[0]);
  const [accountCurrency, setAccountCurrency] = React.useState<Option>(majorCurrencies[0]);
  const [tradeSize, setTradeSize] = React.useState(1);
  const [tradeSizeMeasurement, setTradeSizeMeasurement] = React.useState<TradeSizeMeasurement>(
    TradeSizeMeasurement.Lots,
  );
  const [direction, setDirection] = React.useState<Direction>(Direction.Buy);
  const [openPrice, setOpenPrice] = React.useState(0);
  const [closePrice, setClosePrice] = React.useState(0);
  const [profit, setProfit] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);

  const isDirectionBuy = direction === 'Buy';
  const isDirectionSell = direction === 'Sell';
  const isTradeSizeUnits = tradeSizeMeasurement === 'Units';
  const isTradeSizeLots = tradeSizeMeasurement === 'Lots';

  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(ForexManager);

  React.useEffect(() => {
    const handleFetchExchangeRates = async () => {
      setLoading(true);
      const exchangeRatesRes = await quotesManager.getExchangeRates('USD');
      if (exchangeRatesRes?.ok) {
        const newExchangeRates = exchangeRatesRes.ok.rates;
        setExchangeRates(newExchangeRates);
      }
    };
    handleFetchExchangeRates();
  }, [quotesManager]);

  const handleFetchCurrencyPairQuote = React.useCallback(async () => {
    setLoading(true);
    const currencyPairQuoteRes = await quotesManager.getCurrencyPairQuote(currencyPair?.value ?? '');
    if (currencyPairQuoteRes?.ok) {
      const open = currencyPairQuoteRes.ok.o;
      const close = currencyPairQuoteRes.ok.c;
      setOpenPrice(open);
      setClosePrice(close);
      setLoading(false);
    }
  }, [currencyPair?.value, quotesManager]);

  React.useEffect(() => {
    handleFetchCurrencyPairQuote();
  }, [handleFetchCurrencyPairQuote]);

  const calculate = React.useCallback(
    (newValue: CalculateResultParams) => {
      const newOpenPrice = newValue.openPrice ?? openPrice;
      const newClosePrice = newValue.closePrice ?? closePrice;
      const newTradeSize = newValue.tradeSize ?? tradeSize;
      const newDirection = newValue.direction ?? direction;
      const newTradeSizeMeasurement = newValue.tradeSizeMeasurement ?? tradeSizeMeasurement;
      const newAccountCurrency = newValue.accountCurrency ?? accountCurrency.value;

      const tradeSizeConverted = newTradeSizeMeasurement === 'Lots' ? newTradeSize * lotSizeInUnits : newTradeSize;
      const buyResult = newClosePrice * tradeSizeConverted - newOpenPrice * tradeSizeConverted;
      const sellResult = newOpenPrice * tradeSizeConverted - newClosePrice * tradeSizeConverted;
      const exchangeRate = exchangeRates[newAccountCurrency];
      const result = (newDirection === 'Buy' ? buyResult : sellResult) * (exchangeRate ? exchangeRate : 1);
      setProfit(result);
    },
    [accountCurrency, closePrice, direction, exchangeRates, openPrice, tradeSize, tradeSizeMeasurement],
  );

  const handleChangeCurrencyPair = React.useCallback(
    (option: SingleValue<Option>) => {
      calculate({ currencyPair: option?.value });
      setCurrencyPair(option);
    },
    [calculate],
  );

  const handleChangeAccountCurrency = React.useCallback(
    (option: SingleValue<Option>) => {
      if (option) {
        calculate({ accountCurrency: option?.value });
        setAccountCurrency(option);
      }
    },
    [calculate],
  );

  const handleChangeTradeSizeMeasurement = React.useCallback(
    (value: TradeSizeMeasurement) => {
      if (value) {
        if (tradeSizeMeasurement === 'Lots' && value === 'Units') {
          const newTradeSize = tradeSize * lotSizeInUnits;
          calculate({ tradeSizeMeasurement: value });
          setTradeSize(newTradeSize);
        } else if (tradeSizeMeasurement === 'Units' && value === 'Lots') {
          const newTradeSize = tradeSize / lotSizeInUnits;
          calculate({ tradeSizeMeasurement: value });
          setTradeSize(newTradeSize);
        }
        setTradeSizeMeasurement(value);
      }
    },
    [calculate, tradeSize, tradeSizeMeasurement],
  );

  const handleChangeTradeSize = React.useCallback(
    (value: number) => {
      if (value) {
        calculate({ tradeSize: value });
        setTradeSize(value);
      }
    },
    [calculate],
  );

  const handleChangeOpenPrice = React.useCallback(
    (value: number) => {
      if (value) {
        calculate({ openPrice: value });
        setOpenPrice(value);
      }
    },
    [calculate],
  );

  const handleChangeClosePrice = React.useCallback(
    (value: number) => {
      if (value) {
        calculate({ closePrice: value });
        setClosePrice(value);
      }
    },
    [calculate],
  );

  const handleChangeType = (value: Direction) => {
    calculate({ direction: value });
    setDirection(value);
  };

  const handleResetFields = React.useCallback(() => {
    const tradeSizeConverted = tradeSizeMeasurement === 'Lots' ? 1 : lotSizeInUnits;
    setTradeSize(tradeSizeConverted);
    handleFetchCurrencyPairQuote();
  }, [handleFetchCurrencyPairQuote, tradeSizeMeasurement]);

  const formatPrice = React.useCallback((value: number, currency = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      currency,
      style: 'currency',
    });
    return formatter.format(value);
  }, []);

  return (
    <ForexProfitCalculatorContainer className="forex-profit-calulator-container">
      <div className="buy-or-sell-buttons-container inputs-container">
        <InputWrapper name="Buy or Sell">
          <div className="input-type-container">
            <button className={`${isDirectionBuy ? 'active' : ''}`} onClick={() => handleChangeType(Direction.Buy)}>
              Buy
            </button>
            <button className={`${isDirectionSell ? 'active' : ''}`} onClick={() => handleChangeType(Direction.Sell)}>
              Sell
            </button>
          </div>
        </InputWrapper>
        <InputWrapper name="Trade Size" tooltip="1 Lot = 100,000 Units">
          <div className="input-type-container">
            <button
              className={`${isTradeSizeLots ? 'active' : ''}`}
              onClick={() => handleChangeTradeSizeMeasurement(TradeSizeMeasurement.Lots)}
            >
              Lots
            </button>
            <button
              className={`${isTradeSizeUnits ? 'active' : ''}`}
              onClick={() => handleChangeTradeSizeMeasurement(TradeSizeMeasurement.Units)}
            >
              Units
            </button>
          </div>
        </InputWrapper>
      </div>
      <div className="inputs-container select-inputs-container">
        <SelectContainer
          label="Currency Pair"
          onChange={handleChangeCurrencyPair}
          options={currencyPairs}
          value={currencyPair as Option}
        />
        <SelectContainer
          label="Account Currency"
          onChange={handleChangeAccountCurrency}
          options={majorCurrencies}
          value={accountCurrency}
        />
      </div>
      <div className="inputs-container">
        <InputContainer name={`Trade Size`} onChange={handleChangeTradeSize} value={tradeSize} />
        <InputContainer decimalScale={6} name="Open Price" onChange={handleChangeOpenPrice} value={openPrice} />
        <InputContainer decimalScale={6} name="Close Price" onChange={handleChangeClosePrice} value={closePrice} />
      </div>
      <div className="forex-profit-calculator-footer">
        <div className="result">
          <h5>Profit {loading && <span className="loading-text">(...loading)</span>}</h5>
          <span className="result-price">{formatPrice(profit ?? 0, accountCurrency.value)}</span>
        </div>
        <Button className="reset-button" onClick={handleResetFields}>
          Reset
        </Button>
      </div>
    </ForexProfitCalculatorContainer>
  );
};

const ForexProfitCalculatorContainer = styled.div`
  &.forex-profit-calulator-container {
    .inputs-container {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .buy-or-sell-buttons-container {
      flex-direction: row;
      gap: 0 20px;
      flex-wrap: wrap;

      .input-container {
        width: 100%;
        max-width: 250px;
      }
    }

    .select-inputs-container {
      @media screen and (min-width: 375px) {
        display: flex;
        flex-direction: row;
        gap: 0 10px;
      }

      .select-wrapper {
        width: 100%;
      }
    }

    .input-box {
      margin-bottom: 10px;

      input {
        width: 100%;
      }
    }

    .tooltip {
      font-size: 0.8rem;
      color: ${({ theme }) => theme.colorPalette.gray400};
      margin-left: 10px;
    }

    .inputs-container {
      width: 100%;

      .input-type-container {
        height: 40px;
        display: flex;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        overflow: hidden;
        box-sizing: border-box;

        button {
          width: 100%;
          height: 100%;
          color: ${({ theme }) => theme.colors.brandLight};
          background: ${({ theme }) => theme.colorPalette.white};
          overflow: hidden;
          border: 2px solid ${({ theme }) => theme.colors.border};
          border-radius: ${({ theme }) => theme.borderRadius.default};
          font-weight: ${({ theme }) => theme.fontWeight.semibold};

          &:nth-of-type(1) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right-width: 0;
          }

          &:nth-of-type(2) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left-width: 0;
          }

          &.active {
            color: ${({ theme }) => theme.colorPalette.white};
            background: ${({ theme }) => theme.colors.brand};
          }
        }
      }
    }

    .forex-profit-calculator-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: 1px solid #99aecc;
      background: #b8c0c638;
      padding: 1rem;
      margin-top: 1rem;
      padding-top: 1rem;

      h5 {
        text-transform: uppercase;
        color: #5b7292;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
      }

      .result {
        display: flex;
        flex-direction: column;

        .result-price {
          font-size: ${({ theme }) => theme.fontSize['4xl']};
          color: ${({ theme }) => theme.colors.foreground};
        }

        .loading-text {
          font-size: ${({ theme }) => theme.fontSize.sm};
        }
      }
    }
  }
`;
